/**
 * Handler: add_to_wishlist event
 */

import type { WindowAnalyzify } from '../../types/window-analyzify';
import type { StorefrontProduct } from '../../types/storefront';
import { adaptProduct } from '../../adapters/product';
import { trackKlaviyoAddToWishlistDirect } from '../fallbacks/klaviyo';
import { gtmMarketFields } from '../utils';

export function handleAddToWishlist(
  analyzify: WindowAnalyzify,
  product: StorefrontProduct,
  variantId?: string
): void {
  const { product: adaptedProduct, variant } = adaptProduct(product, variantId);

  // GA4
  if (typeof analyzify.gaAddToWishlist === 'function') {
    analyzify.gaAddToWishlist({ product: adaptedProduct, variant });
  }

  // Facebook
  if (typeof analyzify.fbAddWishList === 'function') {
    analyzify.fbAddWishList({ product: adaptedProduct, variant });
  }

  // GTM - try CDN first, fall back to direct dataLayer push
  const win = window as any;
  let gtmTracked = false;
  if (typeof analyzify.gtmAddToWishList === 'function') {
    try {
      analyzify.gtmAddToWishList({ product: adaptedProduct, variant }, variant.id.toString());
      gtmTracked = true;
    } catch {
      // CDN function failed — fall through to dataLayer push
    }
  }

  if (!gtmTracked && Array.isArray(win.dataLayer) && analyzify.properties?.GTM?.status) {
    const collection = win.analyzify?.collection;
    const variantPrice = analyzify.formatPrice?.(variant.price, true) ?? variant.price / 100;
    const compareAtPrice = variant.compare_at_price ? (analyzify.formatPrice?.(variant.compare_at_price, true) || 0) : 0;
    const getDiscount = (compareAtPrice > 0 && compareAtPrice !== variantPrice) ? compareAtPrice - variantPrice : 0;

    const itemObj: Record<string, any> = {
      id: analyzify.getShopifyId?.(
        analyzify.feed_region || '',
        adaptedProduct.id,
        variant.id,
        variant.sku
      ) || null,
      item_id: adaptedProduct.id?.toString() || null,
      item_name: adaptedProduct.title?.trim() || null,
      price: variantPrice,
      item_category: adaptedProduct.type || null,
      discount: analyzify.formatPrice?.(getDiscount, false) || 0,
      item_barcode: variant.barcode || null,
      item_brand: adaptedProduct.vendor?.trim() || null,
      item_variant: variant.title?.trim() || null,
      item_variant_id: variant.id?.toString() || null,
      item_sku: variant.sku || null,
      quantity: 1,
      index: 0,
    };

    if (collection) itemObj.item_list_id = collection?.id || null;
    if (collection?.title) itemObj.item_list_name = collection?.title || null;

    win.dataLayer.push({ ecommerce: null });
    win.dataLayer.push({
      event: 'ee_add_to_wishlist',
      ss_event_id: analyzify.eventId.get('add_to_wishlist') || null,
      ecommerce: {
        analyzify_source: 'advanced',
        implementation_type: 'extension',
        currency: analyzify.currency || null,
        value: analyzify.formatPrice?.(variantPrice * 1, false) ?? variantPrice,
        cart_id: analyzify.cart_id || null,
        items: [itemObj],
      },
      ...gtmMarketFields(analyzify),
    });
  }

  // Klaviyo - try CDN first, fall back to direct API
  const klaviyoPropsW = analyzify.properties?.KLAVIYO;
  if (klaviyoPropsW?.status && klaviyoPropsW.events?.add_to_wishlist) {
    if (typeof analyzify.klaviyoAddToWishlist === 'function') {
      try {
        analyzify.klaviyoAddToWishlist({ product: adaptedProduct, variant }, variant.id.toString());
      } catch (error) {
        trackKlaviyoAddToWishlistDirect(analyzify, adaptedProduct, variant);
      }
    } else {
      trackKlaviyoAddToWishlistDirect(analyzify, adaptedProduct, variant);
    }
  }

  // Clarity
  if (typeof analyzify.clarityAddToWishlist === 'function') {
    analyzify.clarityAddToWishlist();
  }

  // Hotjar
  if (typeof analyzify.hotjarAddToWishlist === 'function') {
    analyzify.hotjarAddToWishlist();
  }
}
